import Constants from 'js/Constants';
import {
  CLEAR_RESOURCE_SUGGESTIONS,
  GET_ALL_API_KEYS_FAILURE,
  GET_CODE_CONTAINER_LISTING_FAILURE,
  GET_CREDENTIALS_FAILURE,
  GET_DATA_MAPS_FAILURE,
  GET_DATA_SETS_FAILURE,
  GET_DATA_SOURCES_FAILURE,
  GET_PIPELINES_FAILURE,
  GET_PROJECTS_FAILURE,
  GET_SCHEMA_SPEC_LISTING_FAILURE,
  GET_SINKS_FAILURE,
  GET_TAGS_SUCCESS,
  GET_VENDORS_SUCCESS,
  LOGOUT_COMPLETE,
  PAGING_SIGNAL_SUCCESS,
  SEARCH_API_KEYS_FAILURE,
  SEARCH_AVAILABLE_DATASETS_SUCCESS,
  SEARCH_CODE_CONTAINERS_FAILURE,
  SEARCH_CODE_CONTAINERS_SUCCESS,
  SEARCH_CREDENTIALS_FAILURE,
  SEARCH_CREDENTIALS_SUCCESS,
  SEARCH_DATA_MAPS_FAILURE,
  SEARCH_DATA_MAPS_SUCCESS,
  SEARCH_DATA_SETS_FAILURE,
  SEARCH_DATA_SETS_SUCCESS,
  SEARCH_DATA_SOURCES_FAILURE,
  SEARCH_DATA_SOURCES_SUCCESS,
  SEARCH_PIPELINES_FAILURE,
  SEARCH_PIPELINES_SUCCESS,
  SEARCH_PROJECTS_FAILURE,
  SEARCH_PROJECTS_SUCCESS,
  SEARCH_SCHEMA_SPECS_FAILURE,
  SEARCH_SCHEMA_SPECS_SUCCESS,
  SEARCH_SINK_FAILURE,
  SEARCH_SINK_SUCCESS,
  UPDATE_SEARCH_FILTERS,
} from 'js/constants/actionTypes';
import {
  FLOW_TYPE_AUTO,
  FLOW_TYPE_CDC,
  FLOW_TYPE_CUSTOM,
  FLOW_TYPE_ELT,
  FLOW_TYPE_IN_MEMORY,
  FLOW_TYPE_RAG,
  FLOW_TYPE_REPLICATION,
  FLOW_TYPE_SPARK,
  FLOW_TYPE_STREAMING,
  FLOW_TYPE_API_SERVER,
} from 'js/constants/pipelineConstants';
import {
  CONNECTOR_CODE_SQL_RUNNER,
  CONNECTOR_CODE_SQL_RUNNER_DISPLAY_NAME,
} from 'js/constants/connectionCategories';
import {
  DefaultFiltersByView,
  SearchFilterOpTypes,
  SearchFilterTypes,
  SearchFilterViews,
} from 'js/constants/visibilityFilters';
import { forOwn, map, mapKeys, sortBy, toLower, union } from 'lodash';
import { syntheticAvailableFilters } from 'new/utils/syntheticSearchFilters';

function getConnectorFilterLabel(connectorCode, fallbackLabel) {
  return connectorCode === CONNECTOR_CODE_SQL_RUNNER
    ? CONNECTOR_CODE_SQL_RUNNER_DISPLAY_NAME
    : fallbackLabel;
}

const initialState = {
  availableFilters: {
    [SearchFilterTypes.TAG]: {
      type: SearchFilterTypes.TAG,
      label: 'Tag',
      description: 'Labels created by users for facilitating organization of resources',
      availableOpTypes: [
        SearchFilterOpTypes.EQUALS,
        SearchFilterOpTypes.NOT_EQUALS,
        SearchFilterOpTypes.CONTAINS,
        SearchFilterOpTypes.NOT_CONTAINS,
        SearchFilterOpTypes.STARTS_WITH,
        SearchFilterOpTypes.ENDS_WITH,
        SearchFilterOpTypes.IS_EMPTY,
        SearchFilterOpTypes.IS_NOT_EMPTY,
      ],
      availableValues: [],
    },
    [SearchFilterTypes.ID]: {
      type: SearchFilterTypes.ID,
      label: 'ID',
      description: 'Unique identifier of resources listed in this page',
      availableOpTypes: [
        SearchFilterOpTypes.EQUALS,
        SearchFilterOpTypes.NOT_EQUALS,
        SearchFilterOpTypes.GREATER_THAN,
        SearchFilterOpTypes.LESS_THAN,
        SearchFilterOpTypes.CONTAINS,
        SearchFilterOpTypes.NOT_CONTAINS,
        SearchFilterOpTypes.STARTS_WITH,
        SearchFilterOpTypes.ENDS_WITH,
      ],
    },
    [SearchFilterTypes.NAME]: {
      type: SearchFilterTypes.NAME,
      label: 'Name',
      description: 'Name assigned to a resource',
      availableOpTypes: [
        SearchFilterOpTypes.CONTAINS,
        SearchFilterOpTypes.NOT_CONTAINS,
        SearchFilterOpTypes.EQUALS,
        SearchFilterOpTypes.NOT_EQUALS,
        SearchFilterOpTypes.STARTS_WITH,
        SearchFilterOpTypes.ENDS_WITH,
        SearchFilterOpTypes.IS_EMPTY,
        SearchFilterOpTypes.IS_NOT_EMPTY,
      ],
    },
    [SearchFilterTypes.DESCRIPTION]: {
      type: SearchFilterTypes.DESCRIPTION,
      label: 'Description',
      description: 'Description assigned to a resource',
      availableOpTypes: [
        SearchFilterOpTypes.CONTAINS,
        SearchFilterOpTypes.NOT_CONTAINS,
        SearchFilterOpTypes.EQUALS,
        SearchFilterOpTypes.NOT_EQUALS,
        SearchFilterOpTypes.STARTS_WITH,
        SearchFilterOpTypes.ENDS_WITH,
        SearchFilterOpTypes.IS_EMPTY,
        SearchFilterOpTypes.IS_NOT_EMPTY,
      ],
    },
    [SearchFilterTypes.STATUS]: {
      type: SearchFilterTypes.STATUS,
      label: 'Activation Status',
      description: 'Current activation status of a resource',
      availableOpTypes: [SearchFilterOpTypes.EQUALS, SearchFilterOpTypes.NOT_EQUALS],
      availableValues: [
        {
          label: 'Initialized',
          value: toLower(Constants.SourceStatus.INIT),
        },
        {
          label: 'Active',
          value: toLower(Constants.SourceStatus.ACTIVE),
        },
        {
          label: 'Paused',
          value: toLower(Constants.SourceStatus.PAUSED),
        },
        {
          label: 'Billing Tier Limit Exceeded',
          value: toLower(Constants.SourceStatus.RATE_LIMITED),
        },
      ],
    },
    [SearchFilterTypes.FLOW_TYPE]: {
      type: SearchFilterTypes.FLOW_TYPE,
      label: 'Flow Type',
      description: 'Type of Nexla Flow',
      availableOpTypes: [SearchFilterOpTypes.EQUALS, SearchFilterOpTypes.NOT_EQUALS],
      availableValues: [
        {
          label: 'AutoFlow',
          value: FLOW_TYPE_AUTO,
        },
        {
          label: 'FlexFlow',
          value: FLOW_TYPE_STREAMING,
        },
        {
          label: 'Replication',
          value: FLOW_TYPE_REPLICATION,
        },
        {
          label: 'DirectFlow',
          value: FLOW_TYPE_IN_MEMORY,
        },
        {
          label: 'Spark ETL',
          value: FLOW_TYPE_SPARK,
        },
        {
          label: 'DB CDC',
          value: FLOW_TYPE_CDC,
        },
        {
          label: 'ELT',
          value: FLOW_TYPE_ELT,
        },
        {
          label: 'BYO Flow',
          value: FLOW_TYPE_CUSTOM,
        },
        {
          label: 'RAG Flow',
          value: FLOW_TYPE_RAG,
        },
        {
          label: 'API Server',
          value: FLOW_TYPE_API_SERVER,
        },
      ],
    },
    [SearchFilterTypes.SOURCE_ID]: {
      type: SearchFilterTypes.SOURCE_ID,
      description: 'Unique identifier for a Source',
      label: 'Data Source ID',
      availableOpTypes: [
        SearchFilterOpTypes.EQUALS,
        SearchFilterOpTypes.NOT_EQUALS,
        SearchFilterOpTypes.GREATER_THAN,
        SearchFilterOpTypes.LESS_THAN,
        SearchFilterOpTypes.CONTAINS,
        SearchFilterOpTypes.NOT_CONTAINS,
        SearchFilterOpTypes.STARTS_WITH,
        SearchFilterOpTypes.ENDS_WITH,
      ],
    },
    [SearchFilterTypes.CONNECTOR_CODE]: {
      type: SearchFilterTypes.CONNECTOR_CODE,
      label: 'Connector Type',
      description: 'Type of connectors or data systems that are the source or destination of data',
      availableOpTypes: [SearchFilterOpTypes.EQUALS, SearchFilterOpTypes.NOT_EQUALS],
      availableValues: [],
    },
    [SearchFilterTypes.NEXSET_ID]: {
      type: SearchFilterTypes.NEXSET_ID,
      label: 'Nexset ID',
      description: 'Unique identifier of a Nexset',
      availableOpTypes: [
        SearchFilterOpTypes.EQUALS,
        SearchFilterOpTypes.NOT_EQUALS,
        SearchFilterOpTypes.GREATER_THAN,
        SearchFilterOpTypes.LESS_THAN,
        SearchFilterOpTypes.CONTAINS,
        SearchFilterOpTypes.NOT_CONTAINS,
        SearchFilterOpTypes.STARTS_WITH,
        SearchFilterOpTypes.ENDS_WITH,
        SearchFilterOpTypes.IS_EMPTY,
        SearchFilterOpTypes.IS_NOT_EMPTY,
      ],
    },
    [SearchFilterTypes.PARENT_DATA_SET_ID]: {
      type: SearchFilterTypes.PARENT_DATA_SET_ID,
      label: 'Parent Nexset ID',
      description: "Unique identifier of a Nexset's Parent Nexset",
      availableOpTypes: [
        SearchFilterOpTypes.EQUALS,
        SearchFilterOpTypes.NOT_EQUALS,
        SearchFilterOpTypes.GREATER_THAN,
        SearchFilterOpTypes.LESS_THAN,
        SearchFilterOpTypes.CONTAINS,
        SearchFilterOpTypes.NOT_CONTAINS,
        SearchFilterOpTypes.STARTS_WITH,
        SearchFilterOpTypes.ENDS_WITH,
        SearchFilterOpTypes.IS_EMPTY,
        SearchFilterOpTypes.IS_NOT_EMPTY,
      ],
    },
    [SearchFilterTypes.DESTINATION_ID]: {
      type: SearchFilterTypes.DESTINATION_ID,
      label: 'Destination ID',
      description: 'Unique identifier of a Destination',
      availableOpTypes: [
        SearchFilterOpTypes.EQUALS,
        SearchFilterOpTypes.NOT_EQUALS,
        SearchFilterOpTypes.GREATER_THAN,
        SearchFilterOpTypes.LESS_THAN,
        SearchFilterOpTypes.CONTAINS,
        SearchFilterOpTypes.NOT_CONTAINS,
        SearchFilterOpTypes.STARTS_WITH,
        SearchFilterOpTypes.ENDS_WITH,
        SearchFilterOpTypes.IS_EMPTY,
        SearchFilterOpTypes.IS_NOT_EMPTY,
      ],
    },
    [SearchFilterTypes.ATTR_NAME]: {
      type: SearchFilterTypes.ATTR_NAME,
      label: 'Attribute Name',
      description: 'Names of attributes in Nexset schemas',
      availableOpTypes: [
        SearchFilterOpTypes.CONTAINS,
        SearchFilterOpTypes.NOT_CONTAINS,
        SearchFilterOpTypes.EQUALS,
        SearchFilterOpTypes.NOT_EQUALS,
        SearchFilterOpTypes.STARTS_WITH,
        SearchFilterOpTypes.ENDS_WITH,
        SearchFilterOpTypes.IS_EMPTY,
        SearchFilterOpTypes.IS_NOT_EMPTY,
      ],
    },
    [SearchFilterTypes.API_KEY]: {
      type: SearchFilterTypes.API_KEY,
      label: 'API Key',
      availableOpTypes: [
        SearchFilterOpTypes.CONTAINS,
        SearchFilterOpTypes.NOT_CONTAINS,
        SearchFilterOpTypes.EQUALS,
        SearchFilterOpTypes.NOT_EQUALS,
        SearchFilterOpTypes.STARTS_WITH,
        SearchFilterOpTypes.ENDS_WITH,
      ],
    },
    [SearchFilterTypes.RUNTIME_STATUS]: {
      type: SearchFilterTypes.RUNTIME_STATUS,
      label: 'Processing Status',
      description: 'Current processing status of a resource',
      availableOpTypes: [SearchFilterOpTypes.EQUALS, SearchFilterOpTypes.NOT_EQUALS],
      availableValues: [
        {
          label: 'Currently Processing',
          value: 'PROCESSING',
        },
        {
          label: 'Idle',
          value: 'IDLE',
        },
      ],
    },
    // UI-only keys (Archived, Credential Validity, ...) — see new/utils/syntheticSearchFilters
    ...syntheticAvailableFilters,
  },
  filters: {
    [SearchFilterViews.PIPELINES]: DefaultFiltersByView[SearchFilterViews.PIPELINES],
    [SearchFilterViews.PROJECTS]: DefaultFiltersByView[SearchFilterViews.PROJECTS],
    [SearchFilterViews.PROJECT_PIPELINES]:
      DefaultFiltersByView[SearchFilterViews.PROJECT_PIPELINES],
    [SearchFilterViews.SQL_AUTOMATIONS]: DefaultFiltersByView[SearchFilterViews.SQL_AUTOMATIONS],
    [SearchFilterViews.MCP_FLOWS]: DefaultFiltersByView[SearchFilterViews.MCP_FLOWS],
    [SearchFilterViews.SEARCH_PIPELINES]: DefaultFiltersByView[SearchFilterViews.SEARCH_PIPELINES],
    [SearchFilterViews.SOURCES]: DefaultFiltersByView[SearchFilterViews.SOURCES],
    [SearchFilterViews.MY_DATA_SETS]: DefaultFiltersByView[SearchFilterViews.MY_DATA_SETS],
    [SearchFilterViews.AVAILABLE_DATA_SETS]:
      DefaultFiltersByView[SearchFilterViews.AVAILABLE_DATA_SETS],
    [SearchFilterViews.DATA_SINKS]: DefaultFiltersByView[SearchFilterViews.DATA_SINKS],
    [SearchFilterViews.DATA_MAPS]: DefaultFiltersByView[SearchFilterViews.DATA_MAPS],
    [SearchFilterViews.CREDENTIALS]: DefaultFiltersByView[SearchFilterViews.CREDENTIALS],
    [SearchFilterViews.REUSABLE_TRANSFORMS]:
      DefaultFiltersByView[SearchFilterViews.REUSABLE_TRANSFORMS],
    [SearchFilterViews.SCHEMA_SPECS]: DefaultFiltersByView[SearchFilterViews.SCHEMA_SPECS],
    [SearchFilterViews.API_KEYS]: DefaultFiltersByView[SearchFilterViews.API_KEYS],
    [SearchFilterViews.SERVICE_KEYS]: DefaultFiltersByView[SearchFilterViews.SERVICE_KEYS],
    [SearchFilterViews.AI_COMPATIBLE_NEXSETS]:
      DefaultFiltersByView[SearchFilterViews.AI_COMPATIBLE_NEXSETS],
  },
  resourceSuggestions: {
    items: [],
    totalItems: 0,
    lastUpdated: null,
  },
  invalidFields: {},
};

export const visibilityFiltersReducer = (state = initialState, action) => {
  switch (action.type) {
    case UPDATE_SEARCH_FILTERS:
      return updateSearchFilters(state, action);

    case PAGING_SIGNAL_SUCCESS:
      return populateSourceTypeFilters(state, action);

    case GET_VENDORS_SUCCESS:
      return populateVendorSourceTypeFilters(state, action);

    case GET_TAGS_SUCCESS:
      return populateTagFilter(state, action);

    case SEARCH_PROJECTS_SUCCESS:
    case SEARCH_DATA_SOURCES_SUCCESS:
    case SEARCH_DATA_SETS_SUCCESS:
    case SEARCH_SINK_SUCCESS:
    case SEARCH_AVAILABLE_DATASETS_SUCCESS:
    case SEARCH_DATA_MAPS_SUCCESS:
    case SEARCH_CREDENTIALS_SUCCESS:
    case SEARCH_CODE_CONTAINERS_SUCCESS:
    // don't show resource suggestions for API keys because the response is
    // an object which complicates things
    // case SEARCH_API_KEYS_SUCCESS:
    case SEARCH_SCHEMA_SPECS_SUCCESS:
      return addResourceSuggestions(state, action);

    case SEARCH_PIPELINES_SUCCESS:
      return addPipelineResourceSuggestions(state, action);

    case CLEAR_RESOURCE_SUGGESTIONS:
      return clearResourceSuggestions(state, action);

    case GET_PIPELINES_FAILURE:
    case GET_PROJECTS_FAILURE:
    case GET_DATA_SOURCES_FAILURE:
    case GET_DATA_SETS_FAILURE:
    case GET_SINKS_FAILURE:
    case GET_DATA_MAPS_FAILURE:
    case GET_CREDENTIALS_FAILURE:
    case GET_CODE_CONTAINER_LISTING_FAILURE:
    case GET_SCHEMA_SPEC_LISTING_FAILURE:
    case GET_ALL_API_KEYS_FAILURE:

    case SEARCH_PIPELINES_FAILURE:
    case SEARCH_PROJECTS_FAILURE:
    case SEARCH_DATA_SOURCES_FAILURE:
    case SEARCH_DATA_SETS_FAILURE:
    case SEARCH_SINK_FAILURE:
    case SEARCH_DATA_MAPS_FAILURE:
    case SEARCH_CREDENTIALS_FAILURE:
    case SEARCH_CODE_CONTAINERS_FAILURE:
    case SEARCH_SCHEMA_SPECS_FAILURE:
    case SEARCH_API_KEYS_FAILURE:
      return setSearchError(state, action);

    case LOGOUT_COMPLETE:
      // availableFilters have to be preserved during logout because paging
      // doesn't get reloaded at re-login, so source type filter's available
      // values aren't populated after re-login
      return {
        ...state,
        filters: { ...initialState.filters },
      };

    default:
      return state;
  }
};

function updateSearchFilters(state, action) {
  const { view, filters } = action.payload;

  return {
    ...state,
    filters: {
      ...state.filters,
      [view]: filters,
    },
  };
}

function populateSourceTypeFilters(state, action) {
  const { paging } = action;
  const { availableFilters } = state;
  let availableSourceValues = [
    ...availableFilters[SearchFilterTypes.CONNECTOR_CODE].availableValues,
  ];
  const availableSourceValuesByValue = mapKeys(availableSourceValues, 'value');

  forOwn(paging, (connectionInfo, connectionType) => {
    if (!availableSourceValuesByValue[connectionType]) {
      availableSourceValues.push({
        label: getConnectorFilterLabel(connectionType, connectionInfo.meta?.displayName),
        value: connectionType,
      });
    }
  });

  availableSourceValues = map(availableSourceValues, (item) => ({
    ...item,
    label: getConnectorFilterLabel(item.value, item.label),
  }));
  availableSourceValues = sortBy(availableSourceValues, ['label']);

  return {
    ...state,
    availableFilters: {
      ...availableFilters,
      [SearchFilterTypes.CONNECTOR_CODE]: {
        ...availableFilters[SearchFilterTypes.CONNECTOR_CODE],
        availableValues: availableSourceValues,
      },
    },
  };
}

function populateVendorSourceTypeFilters(state, action) {
  const {
    entities: { vendors },
  } = action;
  const { availableFilters } = state;
  let availableSourceValues = [
    ...availableFilters[SearchFilterTypes.CONNECTOR_CODE].availableValues,
  ];
  const availableSourceValuesByValue = mapKeys(availableSourceValues, 'value');

  forOwn(vendors, ({ name, display_name }) => {
    if (!availableSourceValuesByValue[name]) {
      availableSourceValues.push({
        label: getConnectorFilterLabel(name, display_name),
        value: name,
      });
    }
  });

  availableSourceValues = map(availableSourceValues, (item) => ({
    ...item,
    label: getConnectorFilterLabel(item.value, item.label),
  }));
  availableSourceValues = sortBy(availableSourceValues, ['label']);

  return {
    ...state,
    availableFilters: {
      ...availableFilters,
      [SearchFilterTypes.CONNECTOR_CODE]: {
        ...availableFilters[SearchFilterTypes.CONNECTOR_CODE],
        availableValues: availableSourceValues,
      },
    },
  };
}

function populateTagFilter(state, action) {
  const { result } = action;
  const { availableFilters } = state;
  const tagFilter = availableFilters[SearchFilterTypes.TAG];

  return {
    ...state,
    availableFilters: {
      ...availableFilters,
      [SearchFilterTypes.TAG]: {
        ...tagFilter,
        availableValues: sortBy(
          map(result, (tag) => ({
            label: tag,
            value: tag,
          })),
        ),
      },
    },
  };
}

function addResourceSuggestions(state, action) {
  const { result, totalItems } = action;

  return {
    ...state,
    resourceSuggestions: {
      items: result,
      totalItems: totalItems || result?.length || 0,
      lastUpdated: Date.now(),
    },
  };
}

function addPipelineResourceSuggestions(state, action) {
  const { result } = action;

  return addResourceSuggestions(state, {
    ...action,
    result: result?.flows?.id || [],
  });
}

function clearResourceSuggestions(state, action) {
  return {
    ...state,
    resourceSuggestions: initialState.resourceSuggestions,
  };
}

function setSearchError(state, action) {
  const { invalidFields } = state;
  const view = action?.requestInfo?.view;
  const { message } = action;
  const match = message?.match(/Attribute '(.+)' doesn't exist in/);

  if (!match || !view) {
    return state;
  }

  return {
    ...state,
    invalidFields: {
      ...invalidFields,
      [view]: union(invalidFields[view], [match[1]]),
    },
  };
}

export default visibilityFiltersReducer;
